export type SchedulePeriodKind = 'explicit' | 'academic-year' | 'unknown';

export interface AcademicYearLike {
    academicYear?: string;
    startWeek?: number;
    endWeek?: number;
    periods?: RawPeriodLike[];
}

export interface RawPeriodLike {
    id?: number | string;
    name?: string;
    startWeek?: number;
    endWeek?: number;
    start_week?: number;
    end_week?: number;
    startWeekYear?: number;
    endWeekYear?: number;
    start_week_year?: number;
    end_week_year?: number;
    year?: number;
    color?: string;
}

export interface ResolvedSchedulePeriod {
    kind: SchedulePeriodKind;
    periodRef: number | null;
    periodId: number | null;
    academicYearStart: number | null;
    label: string;
    startWeek: number | null;
    endWeek: number | null;
    startWeekYear?: number | null;
    endWeekYear?: number | null;
    color?: string;
}

export function parseAcademicYearStart(academicYear?: string): number | null {
    if (!academicYear) return null;
    const match = academicYear.match(/^(\d{4})/);
    return match?.[1] ? parseInt(match[1], 10) : null;
}

function mapWeekToCalendarYearFromStructure(
    weekNumber: number,
    structure: AcademicYearLike
): number {
    const startCal = parseAcademicYearStart(structure.academicYear);
    if (!startCal) {
        return new Date().getFullYear();
    }
    const sw = structure.startWeek ?? 1;
    const ew = structure.endWeek ?? 53;
    if (sw <= ew) {
        return startCal;
    }
    if (weekNumber >= sw) {
        return startCal;
    }
    if (weekNumber <= ew) {
        return startCal + 1;
    }
    return startCal;
}

function inferMinStartYearFromPeriods(periods?: RawPeriodLike[] | null): number | null {
    if (!periods?.length) return null;
    const years = periods
        .map(p => p.year ?? p.startWeekYear ?? p.start_week_year)
        .filter((y): y is number => y != null && !Number.isNaN(Number(y)))
        .map(Number);
    return years.length ? Math.min(...years) : null;
}

function inferWeekYearsFromStructure(
    startWeek: number,
    endWeek: number,
    structure?: AcademicYearLike | null
): { startWeekYear: number; endWeekYear: number } | null {
    const refFromPeriods = inferMinStartYearFromPeriods(structure?.periods);
    if (!structure?.academicYear && refFromPeriods == null) {
        return null;
    }
    const synth: AcademicYearLike = structure?.academicYear
        ? structure
        : {
            ...(structure ?? {}),
            academicYear: `${refFromPeriods}-${(refFromPeriods ?? 0) + 1}`,
            startWeek: structure?.startWeek ?? 1,
            endWeek: structure?.endWeek ?? 53
        };
    const startWeekYear = mapWeekToCalendarYearFromStructure(startWeek, synth);
    const endWeekYear = mapWeekToCalendarYearFromStructure(endWeek, synth);
    return { startWeekYear, endWeekYear };
}

export function normalizePeriod(
    period: RawPeriodLike,
    structure?: AcademicYearLike | null
): ResolvedSchedulePeriod | null {
    const periodId = period.id !== undefined ? Number(period.id) : null;
    const startWeek = period.startWeek ?? period.start_week ?? null;
    const endWeek = period.endWeek ?? period.end_week ?? null;

    if (!periodId || !startWeek || !endWeek) {
        return null;
    }

    let startWeekYear = period.startWeekYear ?? period.start_week_year ?? null;
    let endWeekYear = period.endWeekYear ?? period.end_week_year ?? null;
    if (startWeekYear == null || endWeekYear == null) {
        const inferred = inferWeekYearsFromStructure(startWeek, endWeek, structure);
        if (inferred) {
            if (startWeekYear == null) {
                startWeekYear = inferred.startWeekYear;
            }
            if (endWeekYear == null) {
                endWeekYear = inferred.endWeekYear;
            }
        }
    }

    const academicYearStartFromPeriod = period.year ?? startWeekYear
        ?? parseAcademicYearStart(structure?.academicYear)
        ?? null;

    return {
        kind: 'explicit',
        periodRef: periodId,
        periodId,
        academicYearStart: academicYearStartFromPeriod,
        label: period.name || `Period ${periodId}`,
        startWeek,
        endWeek,
        startWeekYear,
        endWeekYear,
        color: period.color
    };
}

export function createAcademicYearPeriodScope(structure?: AcademicYearLike | null): ResolvedSchedulePeriod | null {
    const fromLabel = parseAcademicYearStart(structure?.academicYear);
    const fromPeriods = inferMinStartYearFromPeriods(structure?.periods);
    const academicYearStart = fromLabel ?? fromPeriods;
    if (academicYearStart == null) {
        return null;
    }

    const startWeek = structure?.startWeek ?? 1;
    const endWeek = structure?.endWeek ?? 53;
    const label =
        structure?.academicYear?.trim()
        || `${academicYearStart}-${academicYearStart + 1}`;

    return {
        kind: 'academic-year',
        periodRef: academicYearStart,
        periodId: null,
        academicYearStart,
        label,
        startWeek,
        endWeek
    };
}

export function resolveScheduleEntryPeriod(
    rawPeriodId: number | string | null | undefined,
    structure?: AcademicYearLike | null
): ResolvedSchedulePeriod {
    const periodRef = rawPeriodId !== null && rawPeriodId !== undefined ? Number(rawPeriodId) : null;
    const academicYearScope = createAcademicYearPeriodScope(structure);

    if (!periodRef || Number.isNaN(periodRef)) {
        return {
            kind: 'unknown',
            periodRef: null,
            periodId: null,
            academicYearStart: academicYearScope?.academicYearStart ?? null,
            label: 'Unknown period',
            startWeek: null,
            endWeek: null,
            startWeekYear: null,
            endWeekYear: null
        };
    }

    const periods = structure?.periods || [];
    for (const rawPeriod of periods) {
        const period = normalizePeriod(rawPeriod, structure);
        if (period && period.periodId === periodRef) {
            return {
                ...period,
                academicYearStart: period.academicYearStart ?? academicYearScope?.academicYearStart ?? null
            };
        }
    }

    return {
        kind: 'unknown',
        periodRef,
        periodId: null,
        academicYearStart: academicYearScope?.academicYearStart ?? null,
        label: `Period ${periodRef}`,
        startWeek: null,
        endWeek: null,
        startWeekYear: null,
        endWeekYear: null
    };
}

export function findFullAcademicYearPeriod(
    legacyYear: number | string | null | undefined,
    structure?: AcademicYearLike | null
): ResolvedSchedulePeriod | null {
    const academicYearScope = createAcademicYearPeriodScope(structure);
    const parsedLegacyYear = legacyYear !== null && legacyYear !== undefined ? Number(legacyYear) : null;

    if (!academicYearScope || !parsedLegacyYear || parsedLegacyYear !== academicYearScope.academicYearStart) {
        return null;
    }

    const periods = structure?.periods || [];
    for (const rawPeriod of periods) {
        const period = normalizePeriod(rawPeriod, structure);
        if (
            period
            && period.startWeek === academicYearScope.startWeek
            && period.endWeek === academicYearScope.endWeek
        ) {
            return {
                ...period,
                kind: 'academic-year',
                academicYearStart: academicYearScope.academicYearStart,
                label: period.label || academicYearScope.label
            };
        }
    }

    return academicYearScope;
}

export function isWeekInWeekRange(week: number, startWeek: number, endWeek: number): boolean {
    if (startWeek <= endWeek) {
        return week >= startWeek && week <= endWeek;
    }

    return week >= startWeek || week <= endWeek;
}

export interface WeekYearCoordinate {
    week: number;
    year: number | null;
}

/** ISO week count for a Gregorian year. 28 December is always in the last ISO week. */
export function isoWeekCountForYear(year: number): 52 | 53 {
    const d = new Date(Date.UTC(year, 11, 28));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const count = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return count === 53 ? 53 : 52;
}

/**
 * Chronological week+year coordinates from (startWeek, startYear) through (endWeek, endYear).
 * Same walk as PeriodManager.getWeeksForWeekRange: continues past the last week of the year
 * (52 or 53, depending on the ISO year) into week 1 of the next year.
 *
 * Example: w37 2025 → w10 2026 yields 37,38,…,last week of 2025, then 1,2,…,10.
 */
export function enumerateWeeksInRange(
    startWeek: number,
    startYear: number,
    endWeek: number,
    endYear: number
): Array<{ week: number; year: number }> {
    if (![startWeek, startYear, endWeek, endYear].every(Number.isFinite)) {
        return [];
    }
    if (startWeek < 1 || endWeek < 1) {
        return [];
    }

    const weeks: Array<{ week: number; year: number }> = [];
    let week = startWeek;
    let year = startYear;
    const effectiveEndWeek = Math.min(endWeek, isoWeekCountForYear(endYear));
    const guard = 120;

    for (let i = 0; i < guard; i += 1) {
        const maxWeek = isoWeekCountForYear(year);
        if (week > maxWeek) {
            week = 1;
            year += 1;
        }

        if (year > endYear || (year === endYear && week > effectiveEndWeek)) {
            break;
        }

        weeks.push({ week, year });
        if (week === effectiveEndWeek && year === endYear) {
            break;
        }

        week += 1;
    }

    return weeks;
}

function enumerateWeekNumbersWrapping(startWeek: number, endWeek: number, maxWeek = 53): number[] {
    const weeks: number[] = [];
    if (endWeek >= startWeek) {
        for (let week = startWeek; week <= endWeek; week += 1) {
            weeks.push(week);
        }
        return weeks;
    }

    for (let week = startWeek; week <= maxWeek; week += 1) {
        weeks.push(week);
    }
    for (let week = 1; week <= endWeek; week += 1) {
        weeks.push(week);
    }
    return weeks;
}

/**
 * Weeks in the resolved period (or academic-year fallback), in chronological order.
 * Cross-year frames such as w37–w10 continue through the last week of the start year
 * and then week 1 of the next year.
 */
export function getWeeksForResolvedPeriod(
    period: ResolvedSchedulePeriod | null | undefined,
    structure?: AcademicYearLike | null
): WeekYearCoordinate[] {
    const scoped =
        period?.startWeek != null && period?.endWeek != null
            ? period
            : createAcademicYearPeriodScope(structure);

    if (scoped?.startWeek == null || scoped?.endWeek == null) {
        return [];
    }

    const startYear =
        scoped.startWeekYear
        ?? scoped.academicYearStart
        ?? parseAcademicYearStart(structure?.academicYear)
        ?? null;

    if (startYear != null) {
        const endYear =
            scoped.endWeekYear
            ?? (scoped.startWeek > scoped.endWeek ? startYear + 1 : startYear);
        return enumerateWeeksInRange(scoped.startWeek, startYear, scoped.endWeek, endYear);
    }

    return enumerateWeekNumbersWrapping(scoped.startWeek, scoped.endWeek).map((week) => ({
        week,
        year: null
    }));
}

function weekYearTupleOrder(year: number, week: number): number {
    return year * 100 + week;
}

/** True if (targetYear, targetWeek) lies inside the resolved period bounds (including explicit calendar week years). */
export function isResolvedPeriodContainingWeek(
    resolvedPeriod: ResolvedSchedulePeriod | null | undefined,
    targetWeekNumber: number,
    targetYear: number
): boolean {
    if (!resolvedPeriod || resolvedPeriod.kind === 'unknown' || !resolvedPeriod.startWeek || !resolvedPeriod.endWeek) {
        return true;
    }

    const sy = resolvedPeriod.startWeekYear;
    const ey = resolvedPeriod.endWeekYear;
    if (sy != null && ey != null) {
        const t = weekYearTupleOrder(targetYear, targetWeekNumber);
        return t >= weekYearTupleOrder(sy, resolvedPeriod.startWeek)
            && t <= weekYearTupleOrder(ey, resolvedPeriod.endWeek);
    }

    return isWeekInWeekRange(targetWeekNumber, resolvedPeriod.startWeek, resolvedPeriod.endWeek);
}

export function expandWeekSeries(weekSeries?: string | null): number[] {
    if (!weekSeries || typeof weekSeries !== 'string') {
        return [];
    }

    const weeks = new Set<number>();
    weekSeries.split(',').map(part => part.trim()).filter(Boolean).forEach(part => {
        if (part.includes('-')) {
            const [startRaw, endRaw] = part.split('-');
            const start = parseInt((startRaw || '').trim(), 10);
            const end = parseInt((endRaw || '').trim(), 10);
            if (Number.isNaN(start) || Number.isNaN(end) || start < 1 || start > 53 || end < 1 || end > 53) {
                return;
            }

            if (start <= end) {
                for (let week = start; week <= end; week += 1) {
                    weeks.add(week);
                }
            } else {
                for (let week = start; week <= 53; week += 1) {
                    weeks.add(week);
                }
                for (let week = 1; week <= end; week += 1) {
                    weeks.add(week);
                }
            }
            return;
        }

        const week = parseInt(part, 10);
        if (!Number.isNaN(week) && week >= 1 && week <= 53) {
            weeks.add(week);
        }
    });

    return Array.from(weeks).sort((a, b) => a - b);
}

export function getWeeksOutsideResolvedPeriod(
    weekSeries: string | null | undefined,
    period: ResolvedSchedulePeriod | null | undefined
): number[] {
    if (!period?.startWeek || !period?.endWeek) {
        return [];
    }

    return expandWeekSeries(weekSeries).filter(
        week => !isWeekInWeekRange(week, period.startWeek!, period.endWeek!)
    );
}

/**
 * Encode week numbers as a compact series string.
 * When `orderedFrame` is provided (period chronological order), ranges follow that
 * order so a wrap such as 37…52,1…10 becomes "37-10" instead of "1-10,37-52".
 */
export function weekNumbersToWeekSeriesString(weeks: number[], orderedFrame?: number[]): string {
    const unique = weeks.filter((week, index, arr) => (
        Number.isFinite(week) && week >= 1 && week <= 53 && arr.indexOf(week) === index
    ));
    if (unique.length === 0) {
        return '';
    }

    const selected = new Set(unique);
    const frame = orderedFrame && orderedFrame.length > 0
        ? orderedFrame.filter((week, index, arr) => arr.indexOf(week) === index)
        : [...unique].sort((a, b) => a - b);
    const frameSet = new Set(frame);

    const encodeWalk = (walk: number[]): string => {
        const parts: string[] = [];
        let runStart: number | null = null;
        let runEnd: number | null = null;

        const flush = () => {
            if (runStart == null || runEnd == null) {
                return;
            }
            parts.push(runStart === runEnd ? String(runStart) : `${runStart}-${runEnd}`);
            runStart = null;
            runEnd = null;
        };

        for (const week of walk) {
            if (!selected.has(week)) {
                flush();
                continue;
            }
            if (runStart == null) {
                runStart = week;
                runEnd = week;
            } else {
                runEnd = week;
            }
        }
        flush();
        return parts.join(',');
    };

    const main = encodeWalk(frame);
    const leftover = unique.filter((week) => !frameSet.has(week));
    const leftoverStr = leftover.length > 0
        ? encodeWalk([...leftover].sort((a, b) => a - b))
        : '';

    return [main, leftoverStr].filter(Boolean).join(',');
}

/**
 * Keeps only weeks that fall within the resolved period's week bounds.
 * If the period has no bounds (e.g. academic-year option with no named period), returns the week series unchanged.
 */
export function intersectWeekSeriesWithPeriod(
    weekSeries: string | null | undefined,
    period: ResolvedSchedulePeriod | null | undefined
): string {
    if (!period?.startWeek || !period?.endWeek) {
        return typeof weekSeries === 'string' ? weekSeries : '';
    }

    const expanded = expandWeekSeries(weekSeries);
    const inside = expanded.filter(week =>
        isWeekInWeekRange(week, period.startWeek!, period.endWeek!)
    );
    const frame = getWeeksForResolvedPeriod(period).map((coordinate) => coordinate.week);

    return weekNumbersToWeekSeriesString(inside, frame);
}
